import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './ClientOptInsPage.css';

function ClientOptInsPage() {
  // 1) Instead of `clientId`, grab `slug` from the URL
  const { slug } = useParams();
  
  const [clientDetails, setClientDetails] = useState(null);
  const [optIns, setOptIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 2) First fetch the client by slug
        const clientRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients.php?slug=${slug}`
        );
        if (!clientRes.ok) {
          throw new Error('Failed to fetch client details');
        }
        const clientData = await clientRes.json();
        
        // Ensure we actually got a valid client object with an ID
        if (!clientData?.id) {
          setError('Client not found or missing ID.');
          setLoading(false);
          return;
        }

        setClientDetails(clientData);

        // 3) Now fetch the opt-ins by the numeric client ID
        const numericClientId = clientData.id; 
        const optinsRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/optin.php?client_id=${numericClientId}`
        );
        if (!optinsRes.ok) {
          const errData = await optinsRes.json();
          throw new Error(errData.message || 'Failed to fetch opt-in data.');
        }

        const optinsData = await optinsRes.json();
        
        // If the response includes an array of records, use them
        if (optinsData.records) {
          setOptIns(optinsData.records);
        } else {
          // Possibly no records or a custom message
          setError(optinsData.message || 'No opt-ins found for this client.');
        }
      } catch (err) {
        console.error(err);
        setError(err.message || 'An error occurred while fetching data.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]);

  // Handler to delete an opt-in record by ID
  const handleDelete = async (optInId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this opt-in record?'
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/optin.php?id=${optInId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        // Remove this opt-in from the local state
        setOptIns((prev) => prev.filter((optIn) => optIn.id !== optInId));
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to delete opt-in record.');
      }
    } catch (error) {
      console.error('Error deleting opt-in:', error);
      alert('An error occurred while deleting the opt-in record.');
    }
  };

  // CSV Export functionality
  const exportCSV = () => {
    if (optIns.length === 0) {
      alert('No opt-ins to export');
      return;
    }

    // Define CSV headers
    const headers = ['Name', 'Email', 'Phone', 'Opt-in Email', 'Opt-in SMS'];

    // Helper to properly escape CSV fields
    const csvEscape = (value) => {
      if (typeof value === "string") {
        // Replace any double quotes with two double quotes, then wrap the field in quotes
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build the CSV rows
    const csvRows = [];
    csvRows.push(headers.map(csvEscape).join(',')); // header row

    optIns.forEach((optIn) => {
      const row = [
        optIn.name,
        optIn.email,
        optIn.phone,
        optIn.opt_in_email ? 'Yes' : 'No',
        optIn.opt_in_sms ? 'Yes' : 'No'
      ];
      csvRows.push(row.map(csvEscape).join(','));
    });

    // Join rows with newlines
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);

    // Create a filename based on the client name if available
    const fileName = clientDetails?.name ? `${clientDetails.name}-optins.csv` : "optins.csv";
    link.setAttribute("download", fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <p>Loading opt-ins...</p>;
  }

  if (error) {
    return <p className="error">{error}</p>;
  }

  return (
    <div className="client-optins-container">
      <div className="client-overlay">
        <div className="client-optins-container">
          {clientDetails?.logo && (
            <div className="client-logo-container">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/${clientDetails.logo}`}
                alt={`${clientDetails.name} Logo`}
                className="client-logo"
              />
            </div>
          )}

          <h1>
            {clientDetails?.name
              ? `${clientDetails.name} - Opt-Ins`
              : 'Opt-Ins'}
          </h1>

          {/* CSV Export Button */}
          <button onClick={exportCSV} className="export-csv-button">
            Export CSV
          </button>

          {optIns.length > 0 ? (
            <ul className="optins-list">
              {optIns.map((optIn) => (
                <li key={optIn.id} className="optin-item">
                  <div className="optin-data">
                    <strong>Name:</strong> {optIn.name}
                  </div>
                  <div className="optin-data">
                    <strong>Email:</strong> {optIn.email}
                  </div>
                  <div className="optin-data">
                    <strong>Phone:</strong> {optIn.phone}
                  </div>
                  <div className="optin-data">
                    <strong>Opt-in Email:</strong>{' '}
                    {optIn.opt_in_email ? 'Yes' : 'No'}
                  </div>
                  <div className="optin-data">
                    <strong>Opt-in SMS:</strong>{' '}
                    {optIn.opt_in_sms ? 'Yes' : 'No'}
                  </div>
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(optIn.id)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No opt-ins found for this client.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientOptInsPage;
